import React, { useState, useEffect, useRef } from 'react';
import { Responsive as ResponsiveGridLayout } from 'react-grid-layout';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';

import EnhancedDashboard from './EnhancedDashboard';
import SystemMonitor from './SystemMonitor';
import QueueManager from './QueueManager';
import TerminalActivityLogs from './TerminalActivityLogs';
import AgentChat from './AgentChat';
import PlanningOrchestrator from './PlanningOrchestrator';
import OmnigateUsePreview from './OmnigateUsePreview';
import MCPToolsPanel from './MCPToolsPanel';
import { InteractiveTerminalWebSocket } from './InteractiveTerminalWebSocket';
import { backendService } from '../services/backendService';



interface Props {
    stats: any;
    setStats: any;
    addLog: any;
    queue: any;
    setQueue: any;
    logs: any;
    clearLogs: any;
    handleAgentTool: any;
    setIsMainChatActive: any;
    isAudit1200Mode: any;
    isChatFocusMode: boolean;
}

const DEFAULT_LAYOUT = [
    { i: 'dashboard', x: 0, y: 0, w: 4, h: 6 },
    { i: 'browser', x: 4, y: 0, w: 4, h: 6 },
    { i: 'chat', x: 8, y: 0, w: 4, h: 10 },
    { i: 'monitor', x: 0, y: 6, w: 8, h: 4 },
    { i: 'queue', x: 0, y: 10, w: 4, h: 5 },
    { i: 'events', x: 4, y: 10, w: 4, h: 5 },
    { i: 'mcp', x: 8, y: 10, w: 4, h: 5 },
    { i: 'terminal', x: 0, y: 15, w: 6, h: 8 },
    { i: 'planning', x: 0, y: 23, w: 6, h: 10 },
    { i: 'browser2', x: 6, y: 23, w: 6, h: 10 },
    { i: 'browser3', x: 0, y: 33, w: 12, h: 12 }
];

export const DraggableDashboard: React.FC<Props> = ({
    stats, setStats, addLog, queue, setQueue, logs, clearLogs, handleAgentTool, setIsMainChatActive, isAudit1200Mode, isChatFocusMode
}) => {
    const [layouts, setLayouts] = useState<any>({ lg: DEFAULT_LAYOUT });
    const [mounted, setMounted] = useState(false);
    const [isLocked, setIsLocked] = useState(true);
    const [showToolsModal, setShowToolsModal] = useState(false);
    const [activeComputerUseTaskId, setActiveComputerUseTaskId] = useState<string | null>(null);
    const [browser3Url, setBrowser3Url] = useState('http://localhost:7910/?password=secret');
    const [browser3CustomUrl, setBrowser3CustomUrl] = useState('');
    const [browser3ShowInput, setBrowser3ShowInput] = useState(false);
    const [browser3ChatMode, setBrowser3ChatMode] = useState(false);

    // Custom width provider logic since WidthProvider is removed in react-grid-layout v2+
    const [width, setWidth] = useState(1200);
    const containerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        setMounted(true);
        const saved = localStorage.getItem('omnigate_dashboard_layout');
        if (saved) {
            try {
                const p = JSON.parse(saved);
                if (p) { 
                    Object.keys(p).forEach(k => { 
                        if (Array.isArray(p[k])) {
                            p[k].forEach((i:any) => delete i.static);
                            
                            // Inject any missing panels from DEFAULT_LAYOUT
                            if (k === 'lg' || k === 'md' || k === 'sm') {
                                const existingIds = new Set(p[k].map((item: any) => item.i));
                                const missingPanels = DEFAULT_LAYOUT.filter(item => !existingIds.has(item.i));
                                if (missingPanels.length > 0) {
                                    p[k] = [...p[k], ...missingPanels];
                                }
                            }
                        } 
                    }); 
                }
                setLayouts(p);
            } catch {
                setLayouts({ lg: DEFAULT_LAYOUT });
            }
        }

        if (containerRef.current) {
            setWidth(containerRef.current.clientWidth);
        }

        const resizeObserver = new ResizeObserver(entries => {
            for (let entry of entries) {
                if (entry.contentRect.width > 0) {
                    setWidth(entry.contentRect.width);
                }
            }
        });

        if (containerRef.current) {
            resizeObserver.observe(containerRef.current);
        }

        return () => resizeObserver.disconnect();
    }, []);

    const onLayoutChange = (layout: any, allLayouts: any) => {
        setLayouts(allLayouts);
        localStorage.setItem('omnigate_dashboard_layout', JSON.stringify(allLayouts));
    };

    const resetLayout = () => {
        setLayouts({ lg: DEFAULT_LAYOUT });
        localStorage.removeItem('omnigate_dashboard_layout');
    };

    if (isChatFocusMode) {
        return (
            <div className="w-full max-w-[780px] mx-auto">
                <AgentChat
                    onToolCall={handleAgentTool}
                    onConversationStateChange={setIsMainChatActive}
                    addLog={addLog}
                    auditMode={isAudit1200Mode}
                />
            </div>
        );
    }

    if (!mounted) return null; // prevent hydration mismatch

    return (
        <div ref={containerRef} className="w-full relative min-h-full pb-10 mt-2">
            <div className="flex justify-between items-center sm:mb-4 mb-2 px-2 bg-[#131722]/80 border border-[#2a2e39] rounded py-2">
                <div className="flex items-center gap-2">
                    <button onClick={() => setIsLocked(!isLocked)} className={`text-[10px] px-3 py-1.5 rounded transition-all font-bold ${isLocked ? 'bg-red-500/20 text-red-400 border border-red-500/50 shadow-[0_0_10px_rgba(239,68,68,0.2)]' : 'bg-green-500/20 text-green-400 border border-green-500/50'}`}>
                        <i className={`fas ${isLocked ? 'fa-lock' : 'fa-unlock'} mr-1`}></i> {isLocked ? 'Blokada Włączona' : 'Odblokowane (Przeciągaj)'}
                    </button>
                </div>
                <button onClick={resetLayout} className="text-[10px] bg-slate-800 text-slate-400 hover:text-white px-3 py-1.5 rounded border border-slate-700 transition-colors">
                    <i className="fas fa-sync-alt mr-1"></i> Resetuj Układ
                </button>
            </div>

            <style>{`
                .react-grid-layout { position: relative; transition: height 200ms ease; }
                .react-grid-item { transition: all 200ms ease; transition-property: left, top; }
                .react-grid-item.cssTransforms { transition-property: transform; }
                .react-grid-item.resizing { z-index: 1; will-change: width, height; }
                .react-grid-item.react-draggable-dragging { transition: none; z-index: 3; will-change: transform; }
                .react-grid-item.react-grid-placeholder { background: rgba(168, 85, 247, 0.2); border: 2px dashed rgba(168, 85, 247, 0.5); opacity: 0.5; z-index: 2; transition: none; }
                
                /* Custom drag handle & resizer */
                .drag-handle { cursor: grab; padding: 4px; display: flex; align-items: center; justify-content: center; }
                .drag-handle:active { cursor: grabbing; }
                
                .panel-wrapper { height: 100%; display: flex; flex-direction: column; background: #0a0c10; border: 1px solid #2a2e39; border-radius: 0.5rem; overflow: hidden; }
                .panel-wrapper:hover { border-color: rgba(168, 85, 247, 0.3); }
                
                .panel-content { flex: 1; overflow: auto; }
            `}</style>

            <ResponsiveGridLayout
                className="layout"
                width={width}
                layouts={isLocked ? Object.fromEntries(Object.entries(layouts).map(([k, v]) => [k, v.map((i: any) => ({ ...i, static: true }))])) : layouts}
                breakpoints={{ lg: 1200, md: 996, sm: 768, xs: 480, xxs: 0 }}
                cols={{ lg: 12, md: 10, sm: 6, xs: 4, xxs: 2 }}
                rowHeight={60}
                onLayoutChange={onLayoutChange}
                isDraggable={!isLocked}
                isResizable={!isLocked}
                // @ts-ignore
                draggableHandle=".drag-handle"
                margin={[12, 12]}
            >
                <div key="dashboard" className="panel-wrapper">
                    <div className={`bg-[#131722]/80 border-b border-[#2a2e39] flex justify-between items-center px-3 py-1.5 ${!isLocked ? 'drag-handle' : 'cursor-default'}`}>
                        <span className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Metrics Dashboard</span>
                        <i className={`fas fa-grip-lines text-slate-600 ${isLocked ? 'hidden' : ''}`}></i>
                    </div>
                    <div className="panel-content p-3">
                        <EnhancedDashboard stats={stats} setStats={setStats} addLog={addLog} />
                    </div>
                </div>

                <div key="chat" className="panel-wrapper">
                    <div className={`bg-[#131722]/80 border-b border-[#2a2e39] flex justify-between items-center px-3 py-1.5 ${!isLocked ? 'drag-handle' : 'cursor-default'}`}>
                        <span className="text-[10px] font-bold uppercase tracking-widest text-purple-400">Agent Chat</span>
                        <i className={`fas fa-grip-lines text-slate-600 ${isLocked ? 'hidden' : ''}`}></i>
                    </div>
                    <div className="panel-content min-h-0 bg-[#131722]">
                        <AgentChat
                            onToolCall={handleAgentTool}
                            onConversationStateChange={setIsMainChatActive}
                            addLog={addLog}
                            auditMode={isAudit1200Mode}
                        />
                    </div>
                </div>

                <div key="monitor" className="panel-wrapper">
                    <div className={`bg-[#131722]/80 border-b border-[#2a2e39] flex justify-between items-center px-3 py-1.5 ${!isLocked ? 'drag-handle' : 'cursor-default'}`}>
                        <span className="text-[10px] font-bold uppercase tracking-widest text-slate-400">System Monitor</span>
                        <i className={`fas fa-grip-lines text-slate-600 ${isLocked ? 'hidden' : ''}`}></i>
                    </div>
                    <div className="panel-content p-3">
                        <SystemMonitor stats={stats} setStats={setStats} />
                    </div>
                </div>

                <div key="queue" className="panel-wrapper">
                    <div className={`bg-[#131722]/80 border-b border-[#2a2e39] flex justify-between items-center px-3 py-1.5 ${!isLocked ? 'drag-handle' : 'cursor-default'}`}>
                        <span className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Task Queue</span>
                        <i className={`fas fa-grip-lines text-slate-600 ${isLocked ? 'hidden' : ''}`}></i>
                    </div>
                    <div className="panel-content p-3">
                        <QueueManager queue={queue} onCancel={(id: string) => setQueue((q: any[]) => q.filter(i => i.id !== id))} isFallbackActive={false} />
                    </div>
                </div>

                <div key="events" className="panel-wrapper">
                    <div className={`bg-[#131722]/80 border-b border-[#2a2e39] flex justify-between items-center px-3 py-1.5 ${!isLocked ? 'drag-handle' : 'cursor-default'}`}>
                        <span className="text-[10px] font-bold uppercase tracking-widest text-slate-400">System Events</span>
                        <i className={`fas fa-grip-lines text-slate-600 ${isLocked ? 'hidden' : ''}`}></i>
                    </div>
                    <div className="panel-content p-3">
                        <TerminalActivityLogs logs={logs} onClear={clearLogs} maxHeight="100%" title="System Events" />
                    </div>
                </div>
                <div key="browser" className="panel-wrapper">
                    <div className={`bg-[#131722]/80 border-b border-[#2a2e39] flex justify-between items-center px-3 py-1.5 ${!isLocked ? 'drag-handle' : 'cursor-default'}`}>
                        <span className="text-[10px] font-bold uppercase tracking-widest text-[#4285F4]"><i className="fas fa-globe mr-1"></i> Browser Preview <span className="ml-2 text-[7px] bg-green-500/20 text-green-400 px-1 py-0.5 rounded border border-green-500/50 animate-pulse"><i className="fas fa-circle text-[5px] mr-1"></i>LIVE</span></span>
                        <i className={`fas fa-grip-lines text-slate-600 ${isLocked ? 'hidden' : ''}`}></i>
                    </div>
                    <div className={`panel-content bg-black relative ${!isLocked ? 'pointer-events-none' : ''}`}>
                        <iframe
                            src="http://localhost:7901/?password=secret"
                            className="w-full h-full border-0 absolute inset-0 z-10"
                            title="Browser Preview"
                            sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
                        />
                        {/* W przypadku problemu z polaczeniem, iframe przykryje to tlo - ale gdy by nie zaladowal ramki mamy placeholder */}
                        <div className="absolute inset-0 flex flex-col items-center justify-center text-slate-600 border-2 border-dashed border-[#2a2e39] m-2 rounded opacity-50 z-0">
                            <i className="fas fa-satellite-dish text-3xl mb-2 text-blue-500"></i>
                            <h3 className="text-[10px] font-black uppercase tracking-widest text-slate-300">VNC Signal Lost</h3>
                            <p className="text-[8px] text-slate-500 mt-1">Waiting for browser-use instance...</p>
                            <span className="text-[7px] font-mono mt-2 bg-black px-2 py-1 rounded">omnigate-gpt4free-browser HTTP: 7901</span>
                        </div>
                    </div>
                </div>

                <div key="mcp" className="panel-wrapper">
                    <div className={`bg-[#131722]/80 border-b border-[#2a2e39] flex justify-between items-center px-3 py-1.5 ${!isLocked ? 'drag-handle' : 'cursor-default'}`}>
                        <div className="flex items-center gap-2">
                            <i className="fas fa-plug text-yellow-500"></i>
                            <span className="text-[10px] font-bold uppercase tracking-widest text-slate-400">MCP Quick Actions</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <span className="text-[8px] text-yellow-500 uppercase font-black bg-yellow-500/10 px-1.5 py-0.5 rounded">READY</span>
                            <i className="fas fa-grip-lines text-slate-600"></i>
                        </div>
                    </div>
                    <div className="panel-content p-3 overflow-y-auto custom-scrollbar flex flex-col gap-2">
                        <div className="p-2 border-l-2 border-yellow-500 bg-[#1e222d] shadow-sm">
                            <div className="flex justify-between items-center mb-1">
                                <span className="text-[9px] font-bold text-white uppercase"><i className="fas fa-server text-slate-500 mr-1"></i> desktop-commander</span>
                            </div>
                            <p className="text-[8px] text-slate-400">Bezpośrednie wywołanie prawdziwych narzędzi MCP.</p>
                        </div>

                        <div className="mt-2 text-[9px] text-slate-500 font-bold uppercase tracking-widest border-b border-[#2a2e39] pb-1">System Commands</div>

                        <div className="grid grid-cols-2 gap-2 mt-1">
                            <button onClick={async () => {
                                addLog('INFO', 'Running get_system_info via MCP...');
                                try {
                                    const res = await backendService.executeMcpTool('desktop-commander', 'get_system_info', {});
                                    addLog('AI_ACTION', `System Info: ${JSON.stringify(res).substring(0, 100)}...`);
                                } catch (e) { addLog('ERROR', `get_system_info failed: ${e}`); }
                            }} className="bg-[#1e222d] hover:bg-yellow-500/10 border border-[#2a2e39] hover:border-yellow-500/30 p-2 rounded text-left transition-colors flex flex-col justify-center">
                                <i className="fas fa-microchip text-slate-400 mb-1"></i>
                                <span className="text-[8px] font-bold text-slate-300 uppercase">System Info</span>
                            </button>

                            <button onClick={async () => {
                                addLog('INFO', 'Listing processes via MCP...');
                                try {
                                    const res = await backendService.executeMcpTool('desktop-commander', 'list_processes', {});
                                    addLog('AI_ACTION', `Processes: ${JSON.stringify(res).substring(0, 100)}...`);
                                } catch (e) { addLog('ERROR', `list_processes failed: ${e}`); }
                            }} className="bg-[#1e222d] hover:bg-yellow-500/10 border border-[#2a2e39] hover:border-yellow-500/30 p-2 rounded text-left transition-colors flex flex-col justify-center">
                                <i className="fas fa-tasks text-slate-400 mb-1"></i>
                                <span className="text-[8px] font-bold text-slate-300 uppercase">List Processes</span>
                            </button>

                            <button onClick={async () => {
                                addLog('INFO', 'Listing Docker containers via MCP...');
                                try {
                                    const res = await backendService.executeMcpTool('desktop-commander', 'docker_list', { all: false });
                                    addLog('AI_ACTION', `Docker: ${JSON.stringify(res).substring(0, 100)}...`);
                                } catch (e) { addLog('ERROR', `docker_list failed: ${e}`); }
                            }} className="bg-[#1e222d] hover:bg-yellow-500/10 border border-[#2a2e39] hover:border-yellow-500/30 p-2 rounded text-left transition-colors flex flex-col justify-center">
                                <i className="fab fa-docker text-slate-400 mb-1"></i>
                                <span className="text-[8px] font-bold text-slate-300 uppercase">Docker List</span>
                            </button>

                            <button onClick={async () => {
                                addLog('INFO', 'Listing directory via MCP...');
                                try {
                                    const res = await backendService.executeMcpTool('desktop-commander', 'list_directory', { path: "." });
                                    addLog('AI_ACTION', `Directory: ${JSON.stringify(res).substring(0, 100)}...`);
                                } catch (e) { addLog('ERROR', `list_directory failed: ${e}`); }
                            }} className="bg-[#1e222d] hover:bg-yellow-500/10 border border-[#2a2e39] hover:border-yellow-500/30 p-2 rounded text-left transition-colors flex flex-col justify-center">
                                <i className="fas fa-folder-open text-slate-400 mb-1"></i>
                                <span className="text-[8px] font-bold text-slate-300 uppercase">List Dir</span>
                            </button>
                        </div>

                        <button 
                            onClick={() => setShowToolsModal(true)}
                            className="mt-auto w-full py-2 bg-yellow-600/20 hover:bg-yellow-600/40 border border-yellow-500/30 text-yellow-500 text-[9px] font-black uppercase tracking-widest rounded transition-all"
                        >
                            Open Full Toolkit
                        </button>
                    </div>
                </div>

                <div key="planning" className="panel-wrapper">
                    <div className={`bg-[#131722]/80 border-b border-[#2a2e39] flex justify-between items-center px-3 py-1.5 ${!isLocked ? 'drag-handle' : 'cursor-default'}`}>
                        <span className="text-[10px] font-bold uppercase tracking-widest text-purple-400"><i className="fas fa-brain mr-1"></i> Omnigate Use</span>
                        <i className={`fas fa-grip-lines text-slate-600 ${isLocked ? 'hidden' : ''}`}></i>
                    </div>
                    <div className="panel-content min-h-0 bg-[#0a0c10]">
                        <PlanningOrchestrator addLog={addLog} />
                    </div>
                </div>

                <div key="browser2" className="panel-wrapper">
                    <div className={`bg-[#131722]/80 border-b border-[#2a2e39] flex justify-between items-center px-3 py-1.5 ${!isLocked ? 'drag-handle' : 'cursor-default'}`}>
                        <span className="text-[10px] font-bold uppercase tracking-widest text-blue-400"><i className="fas fa-globe mr-1"></i> Agent Screen <span className="ml-2 text-[7px] bg-blue-500/20 text-blue-400 px-1 py-0.5 rounded border border-blue-500/50 animate-pulse"><i className="fas fa-circle text-[5px] mr-1"></i>LIVE</span></span>
                        <i className={`fas fa-grip-lines text-slate-600 ${isLocked ? 'hidden' : ''}`}></i>
                    </div>
                    <div className="panel-content bg-black">
                        <OmnigateUsePreview taskId={activeComputerUseTaskId} />
                    </div>
                </div>

                <div key="browser3" className="panel-wrapper">
                    <div className={`bg-[#131722]/80 border-b border-[#2a2e39] flex flex-wrap justify-between items-center px-3 py-1.5 gap-2 ${!isLocked ? 'drag-handle' : 'cursor-default'}`}>
                        <span className="text-[10px] font-bold uppercase tracking-widest text-cyan-400">
                            <i className="fas fa-desktop mr-1"></i> Computer Use Preview
                            <span className="ml-2 text-[7px] bg-cyan-500/20 text-cyan-400 px-1 py-0.5 rounded border border-cyan-500/50 animate-pulse">
                                <i className="fas fa-circle text-[5px] mr-1"></i>VNC
                            </span>
                        </span>
                        <div className="flex items-center gap-1 flex-wrap">
                            {/* URL preset buttons */}
                            {[
                                { label: 'VNC Agent', url: 'http://localhost:7910/?password=secret', color: 'cyan' },
                                { label: 'GPT4Free', url: 'http://localhost:7901/?password=secret', color: 'green' },
                                { label: 'Screen 2', url: 'http://localhost:7903/', color: 'purple' },
                                { label: '🤖 Ask Chat', url: 'http://localhost:7910/vnc.html?autoconnect=1&resize=scale&view_only=0', color: 'yellow', isChatMode: true },
                            ].map(opt => (
                                <button
                                    key={opt.url}
                                    onClick={() => { setBrowser3Url(opt.url); setBrowser3ShowInput(false); setBrowser3ChatMode(!!(opt as any).isChatMode); }}
                                    className={`text-[8px] px-2 py-0.5 rounded border font-bold uppercase transition-all ${
                                        browser3Url === opt.url
                                            ? `bg-${opt.color}-500/30 text-${opt.color}-300 border-${opt.color}-500/50`
                                            : 'bg-slate-800 text-slate-400 border-slate-600 hover:border-slate-400'
                                    }`}
                                >{opt.label}</button>
                            ))}
                            <button
                                onClick={() => setBrowser3ShowInput(v => !v)}
                                className="text-[8px] px-2 py-0.5 rounded border font-bold uppercase bg-slate-800 text-slate-400 border-slate-600 hover:border-yellow-500/50 hover:text-yellow-400 transition-all"
                            ><i className="fas fa-link mr-1"></i>Custom</button>
                            <i className={`fas fa-grip-lines text-slate-600 ${isLocked ? 'hidden' : ''}`}></i>
                        </div>
                    </div>
                    {browser3ShowInput && (
                        <div className="flex gap-2 px-3 py-2 bg-[#0d1117] border-b border-[#2a2e39]">
                            <input
                                type="text"
                                value={browser3CustomUrl}
                                onChange={e => setBrowser3CustomUrl(e.target.value)}
                                placeholder="http://localhost:PORT/path"
                                className="flex-1 text-[10px] bg-[#131722] border border-[#2a2e39] text-white px-2 py-1 rounded font-mono outline-none focus:border-yellow-500/50"
                                onKeyDown={e => { if (e.key === 'Enter' && browser3CustomUrl) { setBrowser3Url(browser3CustomUrl); setBrowser3ShowInput(false); } }}
                            />
                            <button
                                onClick={() => { if (browser3CustomUrl) { setBrowser3Url(browser3CustomUrl); setBrowser3ShowInput(false); } }}
                                className="text-[9px] px-3 py-1 bg-yellow-600/30 hover:bg-yellow-600/50 text-yellow-400 border border-yellow-500/30 rounded font-bold uppercase transition-all"
                            >Go</button>
                        </div>
                    )}
                    {browser3ChatMode && (
                        <div className="flex items-center gap-2 px-3 py-1.5 bg-yellow-950/40 border-b border-yellow-500/30 shrink-0">
                            <span className="w-1.5 h-1.5 rounded-full bg-yellow-400 animate-pulse shadow-[0_0_6px_#eab308]" />
                            <span className="text-[8px] font-bold text-yellow-300 uppercase tracking-wider">
                                🔗 SuperAssistant Bridge Aktywny
                            </span>
                            <span className="text-[7px] text-yellow-600 ml-auto font-mono">
                                MCP tool: <span className="text-yellow-400">ask_chat</span> → agent pisze do chatu
                            </span>
                        </div>
                    )}
                    <div className={`panel-content bg-black relative ${!isLocked ? 'pointer-events-none' : ''}`}>
                        <iframe
                            key={browser3Url}
                            src={browser3Url}
                            className="w-full h-full border-0 absolute inset-0 z-10"
                            title="Computer Use Preview"
                            sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
                        />
                        <div className="absolute inset-0 flex flex-col items-center justify-center text-slate-600 border-2 border-dashed border-[#2a2e39] m-2 rounded opacity-40 z-0">
                            {browser3ChatMode ? (
                                <>
                                    <i className="fas fa-robot text-3xl mb-2 text-yellow-600"></i>
                                    <h3 className="text-[10px] font-black uppercase tracking-widest text-yellow-500">Ask Chat Mode</h3>
                                    <p className="text-[8px] text-slate-500 mt-1">VNC → DeepSeek + SuperAssistant Bridge</p>
                                    <span className="text-[7px] font-mono mt-2 bg-black px-2 py-1 rounded text-yellow-700">MCP: ask_chat(message, chat_url)</span>
                                </>
                            ) : (
                                <>
                                    <i className="fas fa-desktop text-3xl mb-2 text-cyan-600"></i>
                                    <h3 className="text-[10px] font-black uppercase tracking-widest text-slate-400">Computer Use Screen</h3>
                                    <p className="text-[8px] text-slate-500 mt-1">VNC / noVNC stream from container</p>
                                    <span className="text-[7px] font-mono mt-2 bg-black px-2 py-1 rounded text-slate-500">{browser3Url}</span>
                                </>
                            )}
                        </div>
                    </div>
                </div>

                <div key="terminal" className="panel-wrapper">
                    <div className={`bg-[#131722]/80 border-b border-[#2a2e39] flex justify-between items-center px-3 py-1.5 ${!isLocked ? 'drag-handle' : 'cursor-default'}`}>
                        <div className="flex items-center gap-2">
                            <i className="fas fa-terminal text-purple-500"></i>
                            <span className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Interactive Terminal</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <span className="text-[8px] text-green-500 uppercase font-black bg-green-500/10 px-1.5 py-0.5 rounded">BASH</span>
                            <i className={`fas fa-grip-lines text-slate-600 ${isLocked ? 'hidden' : ''}`}></i>
                        </div>
                    </div>
                    <div className="panel-content min-h-0">
                        <InteractiveTerminalWebSocket addLog={addLog} />
                    </div>
                </div>

                {/* Full Toolkit Modal */}
                {showToolsModal && (
                    <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4 animate-in fade-in duration-300">
                        <div className="w-full h-full max-w-7xl max-h-[90vh] bg-[#131722] border border-[#2a2e39] rounded-xl overflow-hidden shadow-[0_0_50px_rgba(0,0,0,0.5)] flex flex-col relative">
                            <div className="p-4 border-b border-[#2a2e39] flex justify-between items-center bg-[#1c212d]">
                                <div className="flex items-center gap-3">
                                    <div className="w-8 h-8 rounded bg-yellow-500/20 flex items-center justify-center border border-yellow-500/50">
                                        <i className="fas fa-tools text-yellow-500"></i>
                                    </div>
                                    <div>
                                        <h2 className="text-xl font-black text-white uppercase tracking-tighter">Advanced <span className="text-yellow-500">MCP Toolkit</span></h2>
                                        <p className="text-[10px] text-slate-500 font-bold uppercase tracking-widest mt-0.5">Full access to cross-environment tools</p>
                                    </div>
                                </div>
                                <button 
                                    onClick={() => setShowToolsModal(false)}
                                    className="w-10 h-10 rounded-full bg-slate-800 hover:bg-red-500/20 text-slate-400 hover:text-red-500 transition-all flex items-center justify-center border border-slate-700"
                                >
                                    <i className="fas fa-times"></i>
                                </button>
                            </div>
                            <div className="flex-1 overflow-auto bg-[#0a0c10]">
                                <MCPToolsPanel />
                            </div>
                            <div className="p-3 border-t border-[#2a2e39] bg-[#1c212d] text-center">
                                <span className="text-[9px] text-slate-600 font-bold uppercase tracking-[0.2em]">OmniGate Neural Infrastructure · MCP v4.2</span>
                            </div>
                        </div>
                    </div>
                )}
            </ResponsiveGridLayout>
        </div>
    );
};
